import PagedCollection from 'core/model/PagedCollection';
import $auth from 'app/stores/$auth';
import $notifications from 'app/stores/notifications/$notifications';
import $alerting from 'app/stores/alerting/$alerting';
import $kaizen from 'app/stores/ai/$kaizen';
import InvestigationConfigModel from './InvestigationConfigModel';

class InvestigationConfigCollection extends PagedCollection {
  get model() {
    return InvestigationConfigModel;
  }

  get url() {
    return '/api/ui/investigation-config';
  }

  isRangeCovered(start_time, end_time) {
    const requestedStartTime = start_time ?? null;
    const requestedEndTime = end_time ?? null;
    const fetchedStartTime = this._fetchedStartTime ?? null;
    const fetchedEndTime = this._fetchedEndTime ?? null;

    if (requestedStartTime === fetchedStartTime && requestedEndTime === fetchedEndTime) {
      return true;
    }

    if (requestedEndTime == null) {
      return (
        fetchedEndTime == null &&
        requestedStartTime != null &&
        fetchedStartTime != null &&
        requestedStartTime >= fetchedStartTime
      );
    }

    if (requestedStartTime == null || fetchedStartTime == null || fetchedEndTime == null) {
      return false;
    }

    return requestedStartTime >= fetchedStartTime && requestedEndTime <= fetchedEndTime;
  }

  canReuseCoveredRange(start_time, end_time) {
    if (!this.isRangeCovered(start_time, end_time)) {
      return false;
    }

    return (
      (this.requestStatus === 'fetching' && !!this.fetchPromise) ||
      (this.lastFetched != null && this.lastFetched > Date.now() - this.minFetchInterval)
    );
  }

  /**
   * Fetch investigation configs with pagination
   * @param {Object} options - Fetch options
   * @param {number} [options.offset=0] - Starting offset for pagination
   * @param {number} [options.limit=100] - Number of items to fetch
   * @param {boolean} [options.include_investigations=true] - Include investigations for each config
   * @param {boolean} [options.force=false] - Force refetch even if recently fetched
   * @param {boolean} [options.skipPrefetch=false] - Skip prefetching notifications and policies
   * @returns {Promise}
   */
  fetch(options = {}) {
    // Investigation config is gated behind the aiInvestigation permission; resolve empty without it
    // so no caller hits /api/ui/investigation-config for companies that lack the permission.
    if (!$auth.hasPermission('aiInvestigation.enabled', { overrideForSudo: false })) {
      return Promise.resolve();
    }

    const {
      offset = 0,
      limit = 100,
      include_investigations = true,
      force = false,
      skipPrefetch = this.hasFetched,
      start_time,
      end_time,
      ...fetchOptions
    } = options;

    const hasRangeQuery = start_time != null || end_time != null;

    if (this.hasFetched && !force && hasRangeQuery && this.canReuseCoveredRange(start_time, end_time)) {
      return this.fetchPromise || Promise.resolve();
    }

    this.requestStatus = 'fetching';

    const prefetchPromises = skipPrefetch
      ? []
      : [
          $notifications.collection.fetch({ force }),
          $alerting.policyCollection.fetch({ force }),
          include_investigations &&
            $kaizen.isEnabled &&
            $kaizen.chatSessionCollection.fetch({ force, preserveSelection: true })
        ].filter(Boolean);

    return Promise.all(prefetchPromises)
      .then(() =>
        super.fetch({
          query: { offset, limit, include_investigations, start_time, end_time },
          force,
          ...fetchOptions
        })
      )
      .then((result) => {
        if (hasRangeQuery) {
          this._fetchedStartTime = start_time ?? null;
          this._fetchedEndTime = end_time ?? null;
        }
        return result;
      })
      .catch((error) => {
        this.requestStatus = null;
        throw error;
      });
  }

  /**
   * Override processData to restructure API response for PagedCollection
   * API returns: { configs: [...], total_count: number }
   * PagedCollection expects: { data: [...], pagination: { total_count: number } }
   */
  processData(response) {
    if (response && !Array.isArray(response) && response.configs) {
      const transformedResponse = {
        ...response,
        pagination: {
          total_count: response.total_count
        }
      };
      return super.processData(transformedResponse);
    }

    return super.processData(response);
  }

  /**
   * Deserialize: Extract configs array from response
   */
  deserialize(response) {
    if (Array.isArray(response)) {
      return response;
    }

    return response.configs || [];
  }

  /**
   * Check if a rule has investigation config enabled
   * @param {string} ruleId
   * @returns {boolean}
   */
  isEnabled(ruleId) {
    const config = this.get(ruleId);
    return config?.enabled ?? false;
  }
}

export default InvestigationConfigCollection;
